import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * BHG UTM Persistence
 * -------------------
 * Single source of truth for UTM + click-id attribution.
 *
 * Storage: sessionStorage.bhg_utms, latest-touch (cleared on session end).
 *
 * First touch lives in lib/firstTouch.ts (localStorage.bhg_first_touch), written on the first page load whether or
 * not it carried tags. Until 2026-10-24 this file kept a second, tags-only first-touch copy
 * (localStorage.bhg_first_touch_utms) that nothing read; AccountSettings still clears the old key.
 *
 * Call `useUtmPersistence()` once from `App.tsx` at the router root. It listens for
 * route changes and refreshes sessionStorage whenever new UTM params appear.
 *
 * Read anywhere with `getUtmParams()` or the hook helpers below. UTM values flow
 * into every Supabase write (email_signups, page_views, click_events) and every
 * analytics.ts event, so Meta Pixel & GA4 can close the attribution loop.
 *
 * Captured params:
 *   utm_source, utm_medium, utm_campaign, utm_content, utm_term
 *   fbclid (Meta Pixel click ID — auto-captured, survives cookie bans)
 *   gclid  (Google Ads click ID — same deal)
 *   gbraid, wbraid (Google Ads iOS/Web conversion linkers)
 *   ttclid (TikTok), li_fat_id (LinkedIn), msclkid (Bing)
 */

export interface UtmSnapshot {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  ttclid?: string;
  li_fat_id?: string;
  msclkid?: string;
  /** ISO timestamp the snapshot was captured */
  captured_at?: string;
  /** Landing path that first carried these params */
  landing_path?: string;
  /** document.referrer at capture time */
  referrer?: string;
}

const SESSION_KEY = "bhg_utms";

const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "ttclid",
  "li_fat_id",
  "msclkid",
] as const;

function readFromParams(search: string): Partial<UtmSnapshot> {
  const params = new URLSearchParams(search);
  const out: Partial<UtmSnapshot> = {};
  let hasAny = false;
  for (const key of TRACKED_PARAMS) {
    const val = params.get(key);
    if (val) {
      (out as any)[key] = val;
      hasAny = true;
    }
  }
  return hasAny ? out : {};
}

function safeGet<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function safeSet(storage: Storage, key: string, value: unknown) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Private browsing / quota — silent fail.
  }
}

/**
 * Capture UTMs from the current URL. Merges with existing session snapshot so
 * partial param sets don't wipe each other out.
 */
export function captureUtms(search: string, path: string) {
  if (typeof window === "undefined") return;

  const fresh = readFromParams(search);
  if (Object.keys(fresh).length === 0) return;

  const now = new Date().toISOString();
  const referrer = typeof document !== "undefined" ? document.referrer : "";

  const existing = safeGet<UtmSnapshot>(sessionStorage, SESSION_KEY) || {};
  const merged: UtmSnapshot = {
    ...existing,
    ...fresh,
    captured_at: now,
    landing_path: existing.landing_path || path,
    referrer: existing.referrer || referrer,
  };
  safeSet(sessionStorage, SESSION_KEY, merged);
}

/**
 * Read the current session's UTM snapshot (latest-touch).
 */
export function getUtmParams(): UtmSnapshot {
  if (typeof window === "undefined") return {};
  return safeGet<UtmSnapshot>(sessionStorage, SESSION_KEY) || {};
}

/**
 * Return flat UTM object suitable for Supabase column-level inserts.
 * Only returns the canonical `utm_*` keys — ad platform click-ids are
 * excluded since not every table has those columns.
 */
export function getUtmColumns(): Pick<
  UtmSnapshot,
  "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_term"
> {
  const u = getUtmParams();
  return {
    utm_source: u.utm_source,
    utm_medium: u.utm_medium,
    utm_campaign: u.utm_campaign,
    utm_content: u.utm_content,
    utm_term: u.utm_term,
  };
}

/**
 * Clear session-scoped UTMs. Call after conversion so the next funnel
 * step doesn't keep re-attributing. The first touch (lib/firstTouch.ts) is untouched.
 */
export function clearSessionUtms() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // noop
  }
}

/**
 * React hook — mount once at app root. Captures UTMs on every navigation,
 * not just the initial landing, so redirect-based tracking (e.g. `/lp` → `/`)
 * still attributes correctly.
 */
export function useUtmPersistence() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    captureUtms(search, pathname);
  }, [pathname, search]);
}
